import { Alert } from '@creditornot/cb-alert';
import { Button, IconButton, TextButton } from '@creditornot/cb-button';
import { DataTable } from '@creditornot/cb-data-table';
import { Plus as PlusIcon } from '@creditornot/cb-icons';
import { MoreHorizontal as MoreHorizontalIcon } from '@creditornot/cb-icons';
import { MagnifyingGlass } from '@creditornot/cb-icons';
import { cbSemanticCssVars } from '@creditornot/cb-ingredients';
import { Input } from '@creditornot/cb-input';
import { MenuActionItem } from '@creditornot/cb-menu';
import { LegacyPopover } from '@creditornot/cb-popover';
import { useMutation, useQuery } from '@tanstack/react-query';
import { DateTime } from 'luxon';
import React, { useMemo, useState } from 'react';
import toast from 'react-hot-toast';
import { FormattedMessage, useIntl } from 'react-intl';
import { useHistory, useRouteMatch } from 'react-router-dom';
import styled from 'styled-components';

import { Brand } from 'apis/enterprise/types';
import type { SearchUser } from 'apis/restaurant-api/merchant-admin/user/types';
import {
  addVenuesToVenueGroup,
  createVenueGroup,
  deleteVenueGroup,
  fetchVenueGroups,
  updateVenueGroup,
} from 'apis/venue-groups/api';
import type { Scope, VenueGroup, VenueGroupItem } from 'apis/venue-groups/types';
import { usePluralization } from 'common/hooks';
import { useIsMobile } from 'common/hooks/useIsMobile';
import { Typography as T } from 'common/typography';
import { venueGroupInteractionClickCreateVenueGroupFailed } from 'core/telemetry/avo/Avo';
import { useLanguage } from 'locale';
import { useFetchUsers } from 'modules/brand/hooks/useFetchUsers';
import { getBrandRoute } from 'modules/brand/utils/getBrandRoute';

import { AddVenueGroupModal, FormValues as AddVenueGroupFormValues } from './AddEditVenueGroupModal/AddVenueGroupModal';
import {
  EditVenueGroupModal,
  FormValues as EditVenueGroupFormValues,
} from './AddEditVenueGroupModal/EditVenueGroupModal';
import { DeleteVenueGroupModal } from './DeleteVenueGroupModal';
import { displayUserName } from './utils/displayUserName';
import { onError } from './utils/onError';

const VenueGroupContainer = styled.div`
  background-color: ${cbSemanticCssVars.surfaceMain};
  height: 100%;
  display: flex;
  flex-direction: column;
`;

const FiltersContainer = styled.div<{ isMobile: boolean }>`
  display: flex;
  padding: ${({ isMobile }) => (isMobile ? '12px' : '18px')};
  align-items: ${({ isMobile }) => (isMobile ? 'stretch' : 'center')};
`;

const SearchInput = styled(Input)`
  margin-inline-end: 16px;
`;

const ActionsContainer = styled.div`
  min-width: 120px;
  padding: 8px;
  border-radius: 8px;
  overflow: hidden;
`;

const ActionsMenuItem = styled(MenuActionItem)`
  margin-block-start: 8px;
  margin-block-end: 8px;
`;

const Toolbar = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  border-block-end: 1px solid ${cbSemanticCssVars.borderPrimary};
  padding-block: 16px;
  padding-inline: 24px;
`;

const VenueGroupsCount = styled(T.Body3)`
  color: ${cbSemanticCssVars.textSecondary};
`;

const VenueGroupDescription = styled(T.Small)`
  color: ${cbSemanticCssVars.textSecondary};
`;

const ErrorAlert = styled(Alert).attrs({ variant: 'error' })`
  margin: 24px;
`;

export const ListingVenueGroup = ({
  brand,
  isExperienceFlow = false,
}: {
  brand: Brand;
  isExperienceFlow?: boolean;
}) => {
  const [selectedVenueGroup, setSelectedVenueGroup] = useState<VenueGroup | undefined>(undefined);
  const [isAddVenueGroupOpen, setIsAddVenueGroupOpen] = useState<boolean>(false);
  const [isEditVenueGroupOpen, setIsEditVenueGroupOpen] = useState<boolean>(false);
  const [isDeleteVenueGroupOpen, setIsDeleteVenueGroupOpen] = useState<boolean>(false);
  const { key: locale } = useLanguage();
  const { path: basePath } = useRouteMatch();
  const intl = useIntl();
  const history = useHistory();
  const [searchInput, setSearchInput] = useState<string>('');
  const t = (id: string) => intl.formatMessage({ id });
  const isMobile = useIsMobile();
  const { FormattedPluralMessage } = usePluralization();

  const enterpriseId = brand?.enterpriseId ?? '';
  const brandId = brand?.id ?? '';
  const path = getBrandRoute('venue-groups', enterpriseId, brandId);

  const scope: Scope = {
    id: brandId,
    type: 'BRAND',
  };

  const {
    data: venueGroups = [],
    isLoading,
    error: venueGroupsError,
    refetch,
  } = useQuery({
    queryKey: ['fetchVenueGroups', scope.id, scope.type],
    queryFn: () => fetchVenueGroups(scope),
  });

  const userIds = useMemo(() => venueGroups.map(item => item.venueGroup.modifiedBy), [venueGroups]);

  const { data: users, errors: usersErrors } = useFetchUsers(userIds);

  const usersById = useMemo(
    () =>
      users.reduce((acc: Record<string, SearchUser>, user) => {
        if (user) {
          acc[user.id] = user;
        }
        return acc;
      }, {}),
    [users],
  );

  const mutationAddVenueGroup = useMutation(
    async (values: AddVenueGroupFormValues) => {
      const { venueGroupName, description, venueIds } = values;

      const venueGroup = await createVenueGroup(scope, {
        name: venueGroupName,
        description,
      });

      if (venueIds && venueIds.length > 0) {
        await addVenuesToVenueGroup(venueGroup.id, venueIds);
      }
    },
    {
      onSuccess: () => {
        refetch();
        setIsAddVenueGroupOpen(false);
      },
      onError: error => {
        venueGroupInteractionClickCreateVenueGroupFailed();
        onError(error);
      },
    },
  );

  const mutationUpdateVenueGroup = useMutation(
    async (values: EditVenueGroupFormValues) => {
      if (!selectedVenueGroup) {
        return;
      }

      await updateVenueGroup(selectedVenueGroup.id, {
        name: values.venueGroupName,
        description: values.description,
      });
    },
    {
      onSuccess: () => {
        refetch();
        setIsEditVenueGroupOpen(false);
      },
      onError,
    },
  );

  const mutationDeleteVenueGroup = useMutation(
    async (venueGroupId: string) => {
      await deleteVenueGroup(venueGroupId);
    },
    {
      onSuccess: () => {
        refetch();
        setIsDeleteVenueGroupOpen(false);
      },
      onError,
    },
  );

  const matchingVenueGroups = useMemo(() => {
    return venueGroups.filter(item => {
      return item.venueGroup.name.toLowerCase().includes(searchInput.toLowerCase());
    });
  }, [venueGroups, searchInput]);

  if (usersErrors) {
    toast.error(<FormattedMessage id="venue-groups.error" />, { duration: Infinity });
  }

  if (venueGroupsError) {
    return (
      <ErrorAlert>
        <FormattedMessage id="venue-groups.error" />
      </ErrorAlert>
    );
  }

  const handleRowClick = (venueGroupId: string) => {
    if (isExperienceFlow) {
      history.push(`${basePath}/${venueGroupId}`);
    } else {
      history.push(`${path}/${venueGroupId}`);
    }
  };

  const handleAddVenueGroup = async (values: AddVenueGroupFormValues) => {
    await mutationAddVenueGroup.mutateAsync(values);
  };

  const handleEditVenueGroup = async (values: EditVenueGroupFormValues) => {
    await mutationUpdateVenueGroup.mutateAsync(values);
  };

  const handleDeleteVenueGroup = async () => {
    if (!selectedVenueGroup) {
      return;
    }
    await mutationDeleteVenueGroup.mutateAsync(selectedVenueGroup.id);
  };

  const handleOpenCreateModal = () => {
    setIsAddVenueGroupOpen(true);
  };

  return (
    <VenueGroupContainer>
      <Toolbar>
        {!isExperienceFlow && (
          <T.Heading2>
            <FormattedMessage id="venue-groups.venue-groups" />
          </T.Heading2>
        )}
        <Button onClick={handleOpenCreateModal} variant="primary" size="small" icon={<PlusIcon />}>
          <FormattedMessage id="venue-groups.form.create-venue-group" />
        </Button>
      </Toolbar>
      <FiltersContainer isMobile={isMobile}>
        <SearchInput
          stretch={false}
          onClearClick={() => setSearchInput('')}
          value={searchInput}
          onChange={e => setSearchInput(e.currentTarget.value)}
          placeholder={intl.formatMessage({ id: 'navigation.search' })}
          icon={<MagnifyingGlass />}
        />
        <VenueGroupsCount>
          <FormattedPluralMessage id="venue-groups.venue-groups-count" count={matchingVenueGroups.length} />
        </VenueGroupsCount>
      </FiltersContainer>
      <DataTable<VenueGroupItem>
        highlightSelectedRow
        loading={isLoading}
        emptyMessage={{
          title: t('venue-groups.no-venue-group'),
          footer: searchInput ? null : (
            <TextButton onClick={handleOpenCreateModal} variant="brand" size="small" icon={<PlusIcon />}>
              <FormattedMessage id="venue-groups.form.create-venue-group" />
            </TextButton>
          ),
        }}
        columns={[
          {
            id: 'venue-group',
            name: t('venue-groups.venue-group'),
            render: ({ record: { venueGroup } }) => (
              <div>
                <div>{venueGroup.name}</div>
                <VenueGroupDescription>{venueGroup.description}</VenueGroupDescription>
              </div>
            ),
          },
          {
            id: 'venue-count',
            name: t('venue-groups.venue-count'),
            render: ({ record: { venueCount } }) => venueCount,
          },
          {
            id: 'updated-at',
            name: t('venue-groups.updated-at'),
            render: ({ record: { venueGroup } }) =>
              DateTime.fromISO(venueGroup.modifiedAt).toLocaleString(DateTime.DATETIME_MED),
          },
          {
            id: 'updated-by',
            name: t('venue-groups.updated-by'),
            render: ({ record: { venueGroup } }) => {
              const user = usersById[venueGroup.modifiedBy];
              return user
                ? displayUserName(locale, { fistName: user.name.first_name, lastName: user.name.last_name })
                : '-';
            },
          },
          {
            id: 'action',
            name: '',
            alignContent: 'right',
            render: ({ record: { venueGroup } }) => (
              <div data-test-id="actions-popover">
                <LegacyPopover
                  placement="bottom-end"
                  showOnClick
                  showOnHover={false}
                  renderArrow={false}
                  closeOnClickInside
                  disableContentPadding
                  style={{ display: 'block', lineHeight: 0, maxWidth: 20, margin: 'auto' }}
                  content={
                    <ActionsContainer>
                      <ActionsMenuItem
                        variant="black"
                        onClick={e => {
                          e.stopPropagation();
                          setSelectedVenueGroup(venueGroup);
                          setIsEditVenueGroupOpen(true);
                        }}
                      >
                        <FormattedMessage id="state.edit" />
                      </ActionsMenuItem>
                      <ActionsMenuItem
                        variant="red"
                        onClick={e => {
                          e.stopPropagation();
                          setSelectedVenueGroup(venueGroup);
                          setIsDeleteVenueGroupOpen(true);
                        }}
                      >
                        <FormattedMessage id="state.delete" />
                      </ActionsMenuItem>
                    </ActionsContainer>
                  }
                >
                  <IconButton variant="subdued" size="small" background="transparent">
                    <MoreHorizontalIcon />
                  </IconButton>
                </LegacyPopover>
              </div>
            ),
          },
        ]}
        dataSource={matchingVenueGroups}
        getRowKey={record => record.venueGroup.id}
        onRowClick={handleRowClick}
      />

      <AddVenueGroupModal
        isOpen={isAddVenueGroupOpen}
        onSubmit={handleAddVenueGroup}
        onClose={() => setIsAddVenueGroupOpen(false)}
        brand={brand}
      />

      <EditVenueGroupModal
        isOpen={isEditVenueGroupOpen}
        venueGroup={selectedVenueGroup}
        onSubmit={handleEditVenueGroup}
        onClose={() => setIsEditVenueGroupOpen(false)}
      />

      <DeleteVenueGroupModal
        isOpen={isDeleteVenueGroupOpen}
        venueGroup={selectedVenueGroup}
        onSubmit={handleDeleteVenueGroup}
        onClose={() => setIsDeleteVenueGroupOpen(false)}
      />
    </VenueGroupContainer>
  );
};
